import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ChevronLeft, Briefcase, MapPin, Clock, ExternalLink, 
  Building2, Filter, Search, RefreshCw, Calendar, Users,
  Plane, Star, TrendingUp
} from 'lucide-react';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Havayolu şirketleri
const AIRLINES = [
  { id: 'thy', name: 'Turkish Airlines', color: 'bg-red-500' },
  { id: 'pegasus', name: 'Pegasus', color: 'bg-yellow-500' },
  { id: 'sunexpress', name: 'SunExpress', color: 'bg-orange-500' },
  { id: 'anadolujet', name: 'AnadoluJet', color: 'bg-blue-500' },
  { id: 'emirates', name: 'Emirates', color: 'bg-red-600' },
  { id: 'qatar', name: 'Qatar Airways', color: 'bg-purple-600' },
];

export default function JobListings() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    fetchJobs();
    fetchStats();
  }, [selectedCompany]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = selectedCompany ? `?company=${selectedCompany}` : '';
      const response = await axios.get(`${API}/jobs/listings${params}`, { withCredentials: true });
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/jobs/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const filteredJobs = jobs.filter(job => 
    searchQuery === '' || 
    job.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-sky-900 to-slate-900">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-72 h-72 bg-sky-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <div className="relative z-10 bg-gradient-to-r from-sky-500/90 to-blue-600/90 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Ana Menü</span>
            </button>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Briefcase className="w-6 h-6" />
              Güncel İş İlanları
            </h1>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 -mt-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="border-0 bg-white/10 backdrop-blur-lg">
            <CardContent className="p-4 text-center">
              <Briefcase className="w-8 h-8 text-sky-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.total_active_jobs || 0}</p>
              <p className="text-xs text-white/60">Aktif İlan</p>
            </CardContent>
          </Card>
          <Card className="border-0 bg-white/10 backdrop-blur-lg">
            <CardContent className="p-4 text-center">
              <Building2 className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{Object.keys(stats?.by_company || {}).length}</p>
              <p className="text-xs text-white/60">Şirket</p>
            </CardContent>
          </Card>
          <Card className="border-0 bg-white/10 backdrop-blur-lg">
            <CardContent className="p-4 text-center">
              <Plane className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">6</p>
              <p className="text-xs text-white/60">Havayolu</p>
            </CardContent>
          </Card>
          <Card className="border-0 bg-white/10 backdrop-blur-lg">
            <CardContent className="p-4 text-center">
              <RefreshCw className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-sm font-bold text-white">Günlük</p>
              <p className="text-xs text-white/60">Güncelleme</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 pb-8">
        {/* Filters */}
        <Card className="border-0 bg-white/10 backdrop-blur-lg mb-6">
          <CardContent className="p-4">
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
              <Input
                placeholder="İlan ara... (pozisyon, şirket, lokasyon)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50"
              />
            </div>

            {/* Company Filter */}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={selectedCompany === null ? "default" : "outline"}
                onClick={() => setSelectedCompany(null)}
                className={selectedCompany === null ? "bg-sky-500" : "border-white/30 text-white hover:bg-white/10"}
              >
                Tümü
              </Button>
              {AIRLINES.map((airline) => (
                <Button
                  key={airline.id}
                  size="sm"
                  variant={selectedCompany === airline.name ? "default" : "outline"}
                  onClick={() => setSelectedCompany(selectedCompany === airline.name ? null : airline.name)}
                  className={selectedCompany === airline.name 
                    ? `${airline.color} text-white` 
                    : "border-white/30 text-white hover:bg-white/10"
                  }
                >
                  {airline.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Job Listings */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-12 h-12 text-sky-400 mx-auto mb-4 animate-spin" />
            <p className="text-white/60">İlanlar yükleniyor...</p>
          </div>
        ) : filteredJobs.length > 0 ? (
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <Card 
                key={job.id} 
                className="border-0 bg-white/10 backdrop-blur-lg hover:bg-white/15 transition-all cursor-pointer"
                onClick={() => setSelectedJob(selectedJob?.id === job.id ? null : job)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Logo */}
                    <div className="w-16 h-16 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {job.logo_url ? (
                        <img src={job.logo_url} alt={job.company} className="w-12 h-12 object-contain" />
                      ) : (
                        <Building2 className="w-8 h-8 text-white/50" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-bold text-white">{job.title}</h3>
                          <p className="text-sky-300 font-medium">{job.company}</p>
                        </div>
                        <Badge className="bg-sky-500/20 text-sky-300 border-0">
                          {job.job_type || 'Tam Zamanlı'}
                        </Badge>
                      </div>

                      <div className="flex flex-wrap gap-3 mt-2 text-sm text-white/60">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.location || 'Türkiye'}
                        </span>
                        {job.deadline && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Son: {formatDate(job.deadline)}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatDate(job.created_at)}
                        </span>
                      </div>

                      {/* Expanded Content */}
                      {selectedJob?.id === job.id && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <p className="text-white/80 text-sm mb-4">{job.description}</p>
                          
                          {job.requirements?.length > 0 && (
                            <div className="mb-4">
                              <h4 className="text-white font-medium mb-2">Gereksinimler:</h4>
                              <ul className="list-disc list-inside text-white/70 text-sm space-y-1">
                                {job.requirements.map((req, idx) => (
                                  <li key={idx}>{req}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(job.apply_url, '_blank');
                            }}
                            className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Başvur
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-0 bg-white/10 backdrop-blur-lg">
            <CardContent className="p-12 text-center">
              <Briefcase className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Henüz İlan Bulunamadı</h3>
              <p className="text-white/60 mb-4">
                {selectedCompany 
                  ? `${selectedCompany} için aktif ilan bulunmuyor.`
                  : 'Şu an aktif iş ilanı bulunmuyor.'}
              </p>
              <p className="text-white/40 text-sm">
                İlanlar günlük olarak güncellenmektedir. Lütfen daha sonra tekrar kontrol edin.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="border-0 bg-gradient-to-r from-sky-500/20 to-blue-500/20 backdrop-blur-lg mt-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Star className="w-6 h-6 text-amber-400 flex-shrink-0" />
              <div>
                <h4 className="text-white font-medium mb-1">İpucu</h4>
                <p className="text-white/70 text-sm">
                  İş ilanları THY, Pegasus, SunExpress, AnadoluJet, Emirates ve Qatar Airways kariyer sayfalarından 
                  günlük olarak güncellenmektedir. Fırsatları kaçırmamak için düzenli olarak kontrol edin!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
